/**
 * WEB-3739 юнит 1 и доводка 1 — команды разделов для палитры ⌘K (группа «Действия
 * и настройки», у эталона — поле `commands` разделов, `design/cabinet-rescin/
 * app.jsx` `Palette`). Данные: подпись — ключ словаря `common` (пара ru/en из
 * эталона), значок — имя `Icon` кита из эталона, адрес — страница кабинета, где
 * это делается (глубже — только по адресу, который страница уже понимает),
 * `keywords` — слова поиска эталона и заголовок его раздела (как `kw` эталона).
 * Команды «Баланса» адреса не знают: отметка `balance`, переход — дверью
 * пополнения `useOrgTopUp` (сторож `useOrgTopUp.door.web2952`).
 *
 * Действующий раздел эталона — тот, что страница показывает: у заменённого
 * (`replaces`) команды берутся у заменившего. Команд настроек проекта
 * (`settings-x.jsx`, `SEARCH`) здесь нет: у эталона они только на русском
 * (вопрос в отчёте доводки 1). «Личных настроек» нет: `me-k.jsx` заменил `me`
 * без команд.
 *
 * Видимость — `visibleCommands` (доводка 2, решение оркестратора 28.10: у
 * эталона правила по уровню нет, `app.jsx:440`): команды обоих уровней видны на
 * любом уровне меню, когда раздел команды (`section`, ключ пункта меню) есть в
 * меню её собственного уровня (`level`) и пункт меню, в который ведёт её адрес,
 * тоже виден — те же права и флаг черновика, что у меню (`shellMenu.ts`): так
 * «Пополнить кредиты для ИИ» и команды баланса не видны тому, кому не виден
 * «Баланс», а гостю организации из команд организации видны только «Проекты».
 *
 * Порядок массива — блоками разделов в порядке эталона (регистрация
 * `window.W2ASections`, список загрузки `Кабинет (рескин).dc.html:45`):
 * конструктор, ИИ-студия, аудит, проекты, баланс, дашборд, аналитика, дашборд
 * организации; внутри раздела — порядок `commands` раздела. Подпись раздела
 * справа в строке (`where` эталона, `app.jsx:452`) — `PALETTE_SECTION_TITLE`.
 */
import type { IconName } from '@/components/kit/ds/icons';
import type { commonStrings } from '@/i18n/dictionaries/common';
import {
  orgShellMenu,
  projectShellMenu,
  shellFlat,
  type ShellMenuItem,
  type ShellMenuKey,
} from './shellMenu';

type Common = (typeof commonStrings)['en'];
type StringKey = {
  [K in keyof Common]: Common[K] extends string ? K : never;
}[keyof Common];

export type ShellLevel = 'project' | 'org';

interface PaletteCommandBase {
  id: string;
  label: StringKey;
  icon: IconName;
  keywords: string;
  /** Уровень меню, по которому судится раздел команды (видна она на любом). */
  level: ShellLevel;
  /** Раздел — ключ пункта меню этого уровня. */
  section: ShellMenuKey;
}

/**
 * Куда ведёт команда: адрес страницы — либо «Баланс» отметкой `balance`, без
 * адреса. «Баланс» показывает АКТИВНУЮ организацию, поэтому туда ведёт только
 * дверь пополнения `useOrgTopUp` (сперва переключает организацию проекта;
 * сторож `hooks/useOrgTopUp.door.web2952.guard.test.ts`).
 */
export type PaletteCommand = PaletteCommandBase &
  ({ path: string; balance?: never } | { balance: true; path?: never });

export const PALETTE_COMMANDS: PaletteCommand[] = [
  // Конструктор — `sections/editor.jsx:13`; открывается из списка воронок
  {
    id: 'editor-open',
    label: 'cmdEditorOpen',
    icon: 'funnel',
    path: '/content',
    keywords: 'редактор конструктор экран блок editor builder Конструктор', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'content',
  },
  // ИИ-студия — `sections/ai-studio.jsx:666` (`ai-studio-k.jsx` берёт те же команды); страница вглубь адреса не понимает
  {
    id: 'ai-create',
    label: 'cmdAiCreate',
    icon: 'ai',
    path: '/ai-studio',
    keywords:
      'ии ai генерация студия создать сгенерировать generate studio ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-link',
    label: 'cmdAiLink',
    icon: 'globe',
    path: '/ai-studio',
    keywords: 'стор app store google play ссылка разбор link ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-chat',
    label: 'cmdAiChat',
    icon: 'ai',
    path: '/ai-studio',
    keywords: 'разговор чат вопросы chat conversation ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-plan',
    label: 'cmdAiPlan',
    icon: 'funnel',
    path: '/ai-studio',
    keywords: 'план экраны порядок plan screens ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-mode',
    label: 'cmdAiMode',
    icon: 'bolt',
    path: '/ai-studio',
    keywords: 'pro простой режим mode simple ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-model',
    label: 'cmdAiModel',
    icon: 'ai',
    path: '/ai-studio',
    keywords: 'модель claude gpt openai anthropic model ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-kind',
    label: 'cmdAiKind',
    icon: 'funnel',
    path: '/ai-studio',
    keywords: 'квиз пейволл quiz paywall ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-lang',
    label: 'cmdAiLang',
    icon: 'globe',
    path: '/ai-studio',
    keywords: 'язык language ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-niche',
    label: 'cmdAiNiche',
    icon: 'funnel',
    path: '/ai-studio',
    keywords: 'тематика ниша шаблон основа niche template ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-screens',
    label: 'cmdAiScreens',
    icon: 'funnel',
    path: '/ai-studio',
    keywords: 'экраны число screens count ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-ending',
    label: 'cmdAiEnding',
    icon: 'funnel',
    path: '/ai-studio',
    keywords: 'концовка пейволл лид редирект ending redirect ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-sample',
    label: 'cmdAiSample',
    icon: 'eye',
    path: '/ai-studio',
    keywords: 'скриншот стиль образец картинка screenshot style ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-no-images',
    label: 'cmdAiNoImages',
    icon: 'eyeOff',
    path: '/ai-studio',
    keywords: 'картинки изображения images ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-context',
    label: 'cmdAiContext',
    icon: 'info',
    path: '/ai-studio',
    keywords: 'контекст бренд профиль context brand profile ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-reanalyze',
    label: 'cmdAiReanalyze',
    icon: 'undo',
    path: '/ai-studio',
    keywords: 'анализ стор обновить reanalyze store ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-history',
    label: 'cmdAiHistory',
    icon: 'audit',
    path: '/ai-studio',
    keywords: 'история генерации history ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-running',
    label: 'cmdAiRunning',
    icon: 'bolt',
    path: '/ai-studio',
    keywords: 'идёт генерация прогресс running progress ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  {
    id: 'ai-topup',
    label: 'cmdAiTopup',
    icon: 'bolt',
    balance: true,
    keywords: 'кредиты баланс пополнить credits top up ИИ-студия', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'ai',
  },
  // Аудит — `sections/audit.jsx:318`; страница вкладок адресом не открывает
  {
    id: 'audit-team',
    label: 'cmdAuditTeam',
    icon: 'audit',
    path: '/audit',
    keywords: 'аудит журнал кто что изменил история audit log activity Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  {
    id: 'audit-org-team',
    label: 'cmdAuditOrgTeam',
    icon: 'audit',
    path: '/org/audit',
    keywords: 'аудит организации журнал команда org audit Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'audit',
  },
  {
    id: 'audit-important',
    label: 'cmdAuditImportant',
    icon: 'audit',
    path: '/audit',
    keywords: 'важное роли владение деньги удаление sensitive Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  {
    id: 'audit-export',
    label: 'cmdAuditExport',
    icon: 'audit',
    path: '/audit',
    keywords: 'экспорт журнала csv export log Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  {
    id: 'audit-traffic',
    label: 'cmdAuditTraffic',
    icon: 'audit',
    path: '/audit',
    keywords: 'трафик обрыв стадии установки атрибуция traffic drop Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  {
    id: 'audit-org-traffic',
    label: 'cmdAuditOrgTraffic',
    icon: 'audit',
    path: '/org/audit',
    keywords: 'трафик организации org traffic Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'audit',
  },
  {
    id: 'audit-trace',
    label: 'cmdAuditTrace',
    icon: 'audit',
    path: '/audit',
    keywords: 'трейс путь визитор click_id guid trace debug Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  {
    id: 'audit-lost',
    label: 'cmdAuditLost',
    icon: 'audit',
    path: '/audit',
    keywords: 'потеряшки оборвались lost list выгрузить Аудит', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'audit',
  },
  // Проекты организации — `sections/org.jsx:1065`
  {
    id: 'org-projects-list',
    label: 'cmdOrgProjectsList',
    icon: 'panel',
    path: '/projects',
    keywords: 'список проектов открыть удалить настройки projects Проекты', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'projects',
  },
  {
    id: 'org-projects-create',
    label: 'cmdOrgProjectsCreate',
    icon: 'plus',
    path: '/projects',
    keywords:
      'новый проект приложение app store google play new project Проекты', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'projects',
  },
  // Баланс/Тариф — `sections/org.jsx:1080`; страница вкладок адресом не открывает
  {
    id: 'balance-plan',
    label: 'cmdBalancePlan',
    icon: 'bolt',
    balance: true,
    keywords: 'деньги оплата подписка 299 199 год billing Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-topup',
    label: 'cmdBalanceTopup',
    icon: 'plus',
    balance: true,
    keywords: 'кредиты купить пакет credits buy Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-subscription',
    label: 'cmdBalanceSubscription',
    icon: 'settings',
    balance: true,
    keywords: 'карта отменить stripe портал card cancel Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-usage',
    label: 'cmdBalanceUsage',
    icon: 'chart',
    balance: true,
    keywords: 'объём порог списание 50 usage threshold Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-rates',
    label: 'cmdBalanceRates',
    icon: 'chart',
    balance: true,
    keywords: 'процент поступления цена перехода rates percent Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-history',
    label: 'cmdBalanceHistory',
    icon: 'audit',
    balance: true,
    keywords: 'транзакции списания пополнения history Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-invoices',
    label: 'cmdBalanceInvoices',
    icon: 'audit',
    balance: true,
    keywords: 'инвойсы платежи картой invoices Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-limit',
    label: 'cmdBalanceLimit',
    icon: 'ai',
    balance: true,
    keywords: 'лимит в день в месяц ограничить spend cap Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-from-balance',
    label: 'cmdBalanceFromBalance',
    icon: 'bolt',
    balance: true,
    keywords: 'карта отказала резерв fallback Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  {
    id: 'balance-enterprise',
    label: 'cmdBalanceEnterprise',
    icon: 'mail',
    balance: true,
    keywords: 'энтерпрайз продажи sales Баланс/Тариф', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'balance',
  },
  // Дашборд проекта — `sections/home-x.jsx:582` (заменил `home`)
  {
    id: 'home-ask',
    label: 'cmdHomeAsk',
    icon: 'ai',
    path: '/home',
    keywords: 'ии вопрос сводка почему Дашборд', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'home',
  },
  // Аналитика — `sections/analytics-x.jsx:802` (заменил `analytics`); когорты — вкладка `?tab=cohorts`, её понимает `pages/Analytics.tsx`
  {
    id: 'analytics-countries',
    label: 'cmdAnalyticsCountries',
    icon: 'chart',
    path: '/analytics',
    keywords: 'страны быстрый отчёт Аналитика', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'analytics',
  },
  {
    id: 'analytics-cohorts',
    label: 'cmdAnalyticsCohorts',
    icon: 'chart',
    path: '/analytics?tab=cohorts',
    keywords: 'ltv roi расход Аналитика', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'project',
    section: 'analytics',
  },
  // Дашборд организации — `sections/org-dash-x.jsx:425` (заменил `org-dash` из `org.jsx:1054`)
  {
    id: 'org-dash-money',
    label: 'cmdOrgDashMoney',
    icon: 'chart',
    path: '/org/dashboard',
    keywords: 'дашборд выручка доска Дашборд', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'dash',
  },
  {
    id: 'org-dash-tune',
    label: 'cmdOrgDashTune',
    icon: 'sliders',
    path: '/org/dashboard',
    keywords: 'карточка виджет график блок Дашборд', // i18n-allow: слова поиска эталона (ru и en разом), не надпись
    level: 'org',
    section: 'dash',
  },
];

/**
 * Подпись раздела справа в строке команды — заголовок раздела эталона (`title`
 * в `window.W2ASections`) и его английская пара, ключ словаря `common`.
 */
export const PALETTE_SECTION_TITLE: Partial<Record<ShellMenuKey, StringKey>> = {
  content: 'palWhereContent',
  ai: 'palWhereAi',
  audit: 'palWhereAudit',
  projects: 'palWhereProjects',
  balance: 'palWhereBalance',
  home: 'palWhereHome',
  analytics: 'palWhereAnalytics',
  dash: 'palWhereDash',
};

/** Все пункты обоих уровней, какие вообще бывают (для поиска раздела по адресу). */
const ALL_ITEMS: ShellMenuItem[] = [
  ...shellFlat(projectShellMenu({ ownMoney: false, draft: true })),
  ...shellFlat(orgShellMenu({ guest: false })),
];

const under = (path: string, item: ShellMenuItem) => {
  const p = path.split('?')[0];
  return p === item.path || p.startsWith(`${item.path}/`);
};

/**
 * Команды, видимые при меню `menus` (то, что сейчас рисует меню:
 * `projectShellMenu({ ownMoney, draft })`, `orgShellMenu({ guest })`). Уровень,
 * на котором открыт поиск, в отсев не входит: раздел судится по меню уровня
 * самой команды (`c.level`).
 */
export function visibleCommands(
  cmds: readonly PaletteCommand[],
  menus: { project: ShellMenuItem[][]; org: ShellMenuItem[][] },
): PaletteCommand[] {
  const byLevel = {
    project: shellFlat(menus.project),
    org: shellFlat(menus.org),
  };
  const shown = [...byLevel.project, ...byLevel.org];
  return cmds.filter((c) => {
    if (!byLevel[c.level].some((i) => i.key === c.section)) return false;
    const target = c.balance
      ? ALL_ITEMS.find((i) => i.key === 'balance')
      : ALL_ITEMS.find((i) => under(c.path, i));
    return !target || shown.some((i) => i === target || i.path === target.path);
  });
}
